import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import AdminNav from "@/components/AdminNav";
import LingentFloatingIcon from "@/components/LingentFloatingIcon";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, Loader2, Anchor } from "lucide-react";

const EMPTY = {
  name: "", industry_label: "",
  jargon: [], regulatory_anchors: [], default_hashtags: [],
  cta_template: "", tone_voice: "", research_preset: "", is_active: true,
};

export default function PersonasPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/personas");
      setItems(data);
    } catch { toast.error("Failed to load personas"); }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing({ ...EMPTY }); setOpen(true); };
  const startEdit = (p) => { setEditing({ ...p }); setOpen(true); };

  return (
    <div className="min-h-screen bg-ink text-bone">
      <AdminNav />
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-10">
        <div className="label-mono text-safety">// personas</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tightest mt-2">
          Who are you writing for?
        </h1>
        <p className="mt-3 text-bone/60 max-w-2xl">
          Industry personas inject the right jargon, regulatory anchors, and CTA into every
          generated article. <b>Marine</b> is active by default for WiseWit.
        </p>

        <div className="mt-8 flex justify-between items-center border-b border-hairline pb-3">
          <div className="label-mono text-bone/60">{items.length} personas</div>
          <Button
            onClick={startNew}
            data-testid="persona-new-btn"
            className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono"
          >
            <Plus className="w-4 h-4 mr-2" /> New persona
          </Button>
        </div>

        <div className="mt-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="personas-list">
          {items.map((p) => (
            <div
              key={p.id}
              className={`border ${p.is_active ? "border-safety/40" : "border-hairline"} bg-surface p-5 flex flex-col`}
              data-testid={`persona-card-${p.id}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  {p.name === "Marine" && <Anchor className="w-4 h-4 text-safety" />}
                  <h3 className="font-display text-lg font-bold tracking-tightest">{p.name}</h3>
                </div>
                <div className="flex items-center gap-1">
                  {p.is_seeded && (
                    <span className="label-mono text-[10px] text-bone/40">SEED</span>
                  )}
                  {p.is_active && (
                    <span className="label-mono text-[10px] text-safety">ACTIVE</span>
                  )}
                </div>
              </div>
              <div className="label-mono text-[10px] text-bone/50 mt-1">{p.industry_label}</div>
              <p className="mt-3 text-sm text-bone/70 line-clamp-3">{p.tone_voice}</p>
              <div className="mt-3 flex flex-wrap gap-1">
                {(p.default_hashtags || []).slice(0, 4).map((h, i) => (
                  <span key={i} className="label-mono text-[10px] text-bone/50">{h}</span>
                ))}
              </div>
              <div className="mt-auto pt-4 border-t border-hairline flex gap-2">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => startEdit(p)}
                  data-testid={`persona-edit-${p.id}`}
                  className="rounded-none label-mono hover:bg-bone hover:text-ink h-8"
                >
                  <Edit3 className="w-3 h-3 mr-1" /> Edit
                </Button>
                {!p.is_seeded && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={async () => {
                      if (!window.confirm("Delete persona?")) return;
                      await api.delete(`/personas/${p.id}`);
                      load();
                    }}
                    data-testid={`persona-delete-${p.id}`}
                    className="rounded-none label-mono text-red-500 hover:bg-red-100 h-8 ml-auto"
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </main>
      <LingentFloatingIcon onOpen={() => {}} />

      <PersonaDialog
        open={open}
        editing={editing}
        onClose={() => { setOpen(false); setEditing(null); }}
        onSaved={() => { setOpen(false); setEditing(null); load(); }}
      />
    </div>
  );
}

function PersonaDialog({ open, editing, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (editing) setForm(editing); }, [editing]);
  if (!open || !editing) return null;

  const list = (k) => Array.isArray(form[k]) ? form[k].join(", ") : "";
  const setList = (k, v) =>
    setForm({ ...form, [k]: v.split(",").map((x) => x.trim()).filter(Boolean) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name, industry_label: form.industry_label,
        jargon: form.jargon, regulatory_anchors: form.regulatory_anchors,
        default_hashtags: form.default_hashtags,
        cta_template: form.cta_template, tone_voice: form.tone_voice,
        research_preset: form.research_preset || "",
        is_active: !!form.is_active,
      };
      if (form.id) await api.patch(`/personas/${form.id}`, payload);
      else await api.post("/personas", payload);
      toast.success(form.id ? "Persona updated" : "Persona created");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none bg-ink border-hairline text-bone max-w-2xl max-h-[85vh] overflow-y-auto scrollbar-thin">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tightest">
            {form.id ? "Edit persona" : "New persona"}
          </DialogTitle>
          <DialogDescription className="text-bone/60 text-sm">
            Tells Lingent how to speak to this audience.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-mono text-bone/70">Name</Label>
              <Input
                value={form.name || ""}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 rounded-none bg-surface border-hairline h-10"
                data-testid="persona-name-input"
              />
            </div>
            <div>
              <Label className="label-mono text-bone/70">Industry label</Label>
              <Input
                value={form.industry_label || ""}
                onChange={(e) => setForm({ ...form, industry_label: e.target.value })}
                placeholder="e.g. Maritime & Shipping"
                className="mt-1 rounded-none bg-surface border-hairline h-10"
              />
            </div>
          </div>
          <div>
            <Label className="label-mono text-bone/70">Voice / tone guidance</Label>
            <Textarea
              rows={3}
              value={form.tone_voice || ""}
              onChange={(e) => setForm({ ...form, tone_voice: e.target.value })}
              className="mt-1 rounded-none bg-surface border-hairline"
            />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Jargon (comma separated)</Label>
            <Textarea
              rows={2}
              value={list("jargon")}
              onChange={(e) => setList("jargon", e.target.value)}
              placeholder="drydock, hull fouling, TEU, ..."
              className="mt-1 rounded-none bg-surface border-hairline font-mono text-xs"
            />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Regulatory anchors (comma separated)</Label>
            <Input
              value={list("regulatory_anchors")}
              onChange={(e) => setList("regulatory_anchors", e.target.value)}
              placeholder="IMO 2030, MEPC, ABS, DNV ..."
              className="mt-1 rounded-none bg-surface border-hairline h-10"
            />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Default hashtags (comma separated)</Label>
            <Input
              value={list("default_hashtags")}
              onChange={(e) => setList("default_hashtags", e.target.value)}
              placeholder="#MaritimeAI, #IMO2030 ..."
              className="mt-1 rounded-none bg-surface border-hairline h-10"
            />
          </div>
          <div>
            <Label className="label-mono text-bone/70">CTA template</Label>
            <Textarea
              rows={2}
              value={form.cta_template || ""}
              onChange={(e) => setForm({ ...form, cta_template: e.target.value })}
              className="mt-1 rounded-none bg-surface border-hairline"
            />
          </div>
          <div>
            <Label className="label-mono text-bone/70">Research preset (extra query keywords)</Label>
            <Input
              value={form.research_preset || ""}
              onChange={(e) => setForm({ ...form, research_preset: e.target.value })}
              placeholder="maritime port vessel autonomous IMO ..."
              className="mt-1 rounded-none bg-surface border-hairline h-10"
            />
          </div>
          <div className="flex items-center justify-between border-t border-hairline pt-3">
            <Label className="label-mono text-bone/70">Active</Label>
            <Switch
              checked={!!form.is_active}
              onCheckedChange={(v) => setForm({ ...form, is_active: v })}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}
              className="rounded-none border-hairline bg-transparent label-mono">Cancel</Button>
            <Button onClick={save} disabled={saving}
              data-testid="persona-save-btn"
              className="rounded-none bg-klein hover:bg-klein-hover text-white label-mono">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
